"use client";

import { useEffect, useState } from "react";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useTranslations } from "@/i18n/LocaleProvider";

interface FaqEntry {
  id: string;
  question: string;
  answer: string;
}

// Reads the same faqEntries collection the WhatsApp/Instagram bot matches against (see
// functions/src/faq.ts) -- one place to keep FAQ content current for both channels, rather
// than maintaining separate copies. A client fetch, not build-time data, so an admin edit in
// Firestore shows up here without a redeploy.
// `standalone`: the homepage embeds this between several other similarly-padded sections, where
// that padding reads as the gap between sections -- on its own dedicated /faq page there's no
// previous section to separate from (just the header), so the same py-16 reads as a dead gap
// above the very first heading on the page instead.
export default function FaqSection({ standalone = false }: { standalone?: boolean }) {
  const { t } = useTranslations();
  const [faqs, setFaqs] = useState<FaqEntry[]>([]);
  // Starts true -- without it, this section silently renders nothing (see the `return null`
  // below) for every visitor during the initial fetch, not just a genuinely FAQ-less state,
  // popping the whole section in abruptly (and shifting everything below it) once data lands.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(
      query(collection(db, "faqEntries"), where("isActive", "==", true), orderBy("sortOrder"))
    )
      .then((snap) => {
        setFaqs(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as FaqEntry));
      })
      .catch(() => setFaqs([]))
      .finally(() => setLoading(false));
  }, []);

  // Genuinely no active FAQs (or the fetch failed) -- hide the section entirely, same as
  // before. Only reached once loading has actually finished, never while it's still in flight.
  if (!loading && faqs.length === 0) return null;

  return (
    <section id="faq" className="bg-background">
      <div className={`mx-auto max-w-3xl px-6 ${standalone ? "py-16" : "py-12 sm:py-16 lg:py-20"}`}>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">
          {t("home.faq.eyebrow")}
        </p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl">{t("home.faq.title")}</h2>
        {loading && <p className="mt-10 text-sm text-muted">{t("home.faq.loading")}</p>}
        <div className="mt-10 divide-y divide-foreground/10">
          {faqs.map((faq) => (
            <details key={faq.id} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between font-semibold">
                {faq.question}
                <span className="ml-4 text-accent transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-foreground/70">{faq.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
